import { DISPLAY_FILTER_PARSING_ERROR, type DisplayFilterParsingError } from ".";
import t from "../../../../../i18n";
import { FILE_PARSING_ERROR } from "../../../common/file";
import type { ParsingErrorMessageMap } from "../../../types";

export const DisplayFilterParsingErrorMessages: ParsingErrorMessageMap<
  DISPLAY_FILTER_PARSING_ERROR | FILE_PARSING_ERROR
> = {
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
  [FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED]: () =>
    t("setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded"),
  [FILE_PARSING_ERROR.FILE_INVALID_TYPE]: () =>
    t("setup.diagnosis.observable.import_quantities.error.invalid_format"),
  [DISPLAY_FILTER_PARSING_ERROR.FILE_INVALID_COLUMNS]: () =>
    t("setting.parser.quantity.display_filter.import.error.invalid_columns"),
  [DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_EMPTY]: errorData =>
    t("setting.parser.display_filter.import.error.filter_name_empty", {
      row: Number(errorData.value) || 0,
    }),
  [DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_TOO_LONG]: errorData =>
    t("setting.parser.display_filter.import.error.filter_name_too_long", {
      name: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.NAME_FILTER_IS_NOT_UNIQUE]: errorData => {
    const [firstRow = "0", secondRow = "0"] = (errorData.hint ?? "").split("/");

    return t("setting.parser.quantity.display_filter.import.error.filter_name_not_unique", {
      name: String(errorData.value ?? ""),
      firstRow: Number(firstRow) || 0,
      secondRow: Number(secondRow) || 0,
    });
  },
  [DISPLAY_FILTER_PARSING_ERROR.QUANTITY_IS_EMPTY]: errorData =>
    t("setting.parser.display_filter.import.error.quantity_empty", {
      row: Number(errorData.value) || 0,
    }),
  [DISPLAY_FILTER_PARSING_ERROR.NOT_FOUND_INVALIDATED_QUANTITY]: errorData =>
    t("setting.parser.display_filter.import.error.quantity_not_existing_in_project", {
      quantityName: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.DEFAULT_WITHOUT_GROUP]: errorData =>
    t("setting.parser.display_filter.import.error.default_without_group", {
      name: String(errorData.value ?? ""),
      value: String(errorData.hint ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.INVALID_DEFAULT_VALUE]: errorData =>
    t("setting.parser.display_filter.import.error.invalid_default_value", {
      row: Number(errorData.hint) || 0,
      value: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.GROUP_MULTIPLE_DEFAULTS]: errorData =>
    t("setting.parser.display_filter.import.error.group_multiple_defaults", {
      groupName: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.MIN_SUPERIOR_TO_MAX]: errorData =>
    t("setting.parser.display_filter.import.error.max_inferior_to_min", {
      row: Number(errorData.value) || 0,
    }),
  [DISPLAY_FILTER_PARSING_ERROR.MAX_STD_NEGATIVE]: errorData =>
    t("setting.parser.display_filter.import.error.std_positive", {
      quantityName: String(errorData.hint ?? ""),
      name: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MINIMUM_VALUE]: errorData =>
    t("setting.parser.display_filter.import.error.invalid_number_minimum", {
      row: Number(errorData.hint) || 0,
      value: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MAXIMUM_VALUE]: errorData =>
    t("setting.parser.display_filter.import.error.invalid_number_maximum", {
      row: Number(errorData.hint) || 0,
      value: String(errorData.value ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.INVALID_NUMBER_MAX_STD_VALUE]: errorData =>
    t("setting.parser.display_filter.import.error.invalid_number_maximum_std", {
      name: String(errorData.value ?? ""),
      quantityName: String(errorData.hint ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.INVALID_SOURCE_QUANTITY]: errorData =>
    t("setting.parser.display_filter.import.error.invalid_source_quantity", {
      name: String(errorData.value ?? ""),
      quantityName: String(errorData.hint ?? ""),
    }),
  [DISPLAY_FILTER_PARSING_ERROR.VALUE_ALL_NULL]: errorData =>
    t("setting.parser.display_filter.import.error.least_one_filter_value_required", {
      name: String(errorData.value ?? ""),
      quantityName: String(errorData.hint ?? ""),
    }),
};

export const getDisplayFilterParsingErrorMessage = (error: DisplayFilterParsingError) => {
  const errorMessage = DisplayFilterParsingErrorMessages[error.kind];
  return errorMessage ? errorMessage(error.data) : "Unknown error";
};
